import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Patient, SOAPNoteMetadata } from '../../types';

function initials(first: string, last: string): string {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (!f && !l) return '?';
  return (f.charAt(0) + (l.charAt(0) || '')).toUpperCase();
}

function formatPhone(p: string): string | null {
  if (!p) return null;
  const digits = String(p).replace(/[^0-9]/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return p;
}

function formatDOB(d: string): string | null {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

function truncateEmail(e: string): string | null {
  if (!e) return null;
  return e.length > 26 ? e.slice(0, 23) + '…' : e;
}

interface PatientHeaderProps {
  patient: Patient;
  lastNote: SOAPNoteMetadata | null;
  onEdit: () => void;
  onDelete: () => void;
}

const PatientHeader = ({ patient, lastNote, onEdit, onDelete }: PatientHeaderProps) => {
  const isActive = patient.is_active !== false;
  return (
    <div className='bg-white shadow-sm border border-brand-navy/15 rounded-lg px-4 sm:px-6 py-5 flex items-center gap-5'>
      <div className='h-12 w-12 rounded-full bg-gradient-to-br from-brand-navy to-brand-accent text-white flex items-center justify-center text-lg font-semibold shadow-inner select-none'>
        {initials(patient.first_name, patient.last_name)}
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-3 flex-wrap'>
          <h1 className='text-xl font-semibold text-brand-navy truncate'>{patient.first_name} {patient.last_name}</h1>
          <span className={'px-2 py-0.5 rounded-full text-[10px] font-medium ' + (isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600')}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className='mt-1 text-[11px] text-gray-500 flex flex-wrap gap-x-4 gap-y-1'>
          {patient.date_of_birth && (
            <span title='Date of Birth'>DOB: <span className='text-gray-700'>{formatDOB(patient.date_of_birth)}</span></span>
          )}
          {patient.sex_at_birth && patient.sex_at_birth !== 'unknown' && (
            <span title='Sex at Birth'>Sex: <span className='text-gray-700 capitalize'>{patient.sex_at_birth}</span></span>
          )}
          {patient.email && (
            <span title={patient.email}>Email: <span className='text-gray-700'>{truncateEmail(patient.email)}</span></span>
          )}
          {patient.phone && (
            <span title={patient.phone}>Phone: <span className='text-gray-700'>{formatPhone(patient.phone)}</span></span>
          )}
        </div>
        {lastNote && (
          <div className='text-[10px] text-gray-400 mt-1'>
            Last note: {new Date(lastNote.created_at).toLocaleString()} • {(lastNote.note_type || '').toUpperCase()}
          </div>
        )}
      </div>
      <div className='flex items-center gap-2'>
        <button
          type='button'
          onClick={onEdit}
          className='px-3 py-2 rounded border border-brand-navy/15 bg-white text-xs text-brand-navy hover:bg-brand-navy/5 focus:outline-none focus:ring-2 focus:ring-brand-accent'
        >
          Edit
        </button>
        <button
          type='button'
          onClick={onDelete}
          title='Delete patient'
          className='p-2 rounded border border-red-200 text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300'
          aria-label='Delete patient'
        >
          <TrashIcon className='h-4 w-4' />
        </button>
      </div>
    </div>
  );
};

export default PatientHeader;
